import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSchedulePoller } from './hooks/useSchedulePoller';
import { useParticipant } from './hooks/useParticipant';
import { timeSync } from './engine/TimeSync';
import { CueEngine } from './engine/CueEngine';
import { SHOW_STATES } from './state/showStateMachine';
import {
  API,
  SCHEDULE_POLL_INTERVAL,
  PIPE_BASE_URL,
  PLAYING_HEARTBEAT_MS,
} from './config';
import { resolveEndRedirect, resolveEndText, willRedirect } from './lib/endConfig';
import { startJitteredPoll } from './lib/pollJitter';
import { apiFetch } from './deviceId';
import { fetchScheduleOnce, waitForPlaying } from './lib/schedulePolling';
import { zoneSelectionNeeded } from './lib/zoneSelection';
import { wakeLock } from './engine/wakeLock';
import { t } from './i18n';
import Canvas from './components/Canvas';
import Countdown from './components/Countdown';
import ZoneSelector from './components/ZoneSelector';

/**
 * Parse pipe name from URL path: /pipeName
 */
function getPipeName() {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, '');
  return path || null;
}

/**
 * Parse pre-assigned zone/row from URL query params.
 * e.g. /pipeName?zone=2&row=5
 * Zone index is 0-based. Row is passed as-is (matches the row number).
 */
function getUrlAssignment() {
  const params = new URLSearchParams(window.location.search);
  const zone = params.get('zone');
  const row = params.get('row');
  return {
    zone: zone !== null ? parseInt(zone, 10) : null,
    row: row !== null ? parseInt(row, 10) : null,
    noRedirect: params.has('noRedirect'),
    debug1: params.has('debug1'),
    debug2: params.has('debug2'),
    debug3: params.has('debug3'),
  };
}

/**
 * Persist and retrieve zone/row selection per pipe in localStorage.
 * URL params always take priority over stored values.
 */
function getSavedAssignment(pipeName) {
  try {
    const raw = localStorage.getItem(`ls-zone-${pipeName}`);
    if (!raw) return { zone: null, row: null };
    const data = JSON.parse(raw);
    return {
      zone: typeof data.zone === 'number' ? data.zone : null,
      row: typeof data.row === 'number' ? data.row : null,
    };
  } catch {
    return { zone: null, row: null };
  }
}

function saveAssignment(pipeName, zone, row) {
  try {
    localStorage.setItem(`ls-zone-${pipeName}`, JSON.stringify({ zone, row }));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

function Logo() {
  return <img src="/pipeup-logo.svg" alt="Pipeup" style={logoStyle} />;
}

const APP_VERSION = __APP_VERSION__;

// Brightness glyph — thin outline sun (inherits colour via currentColor).
function SunIcon({ size = 38 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="24" cy="24" r="6.5" />
      <line x1="24" y1="5" x2="24" y2="10" />
      <line x1="24" y1="38" x2="24" y2="43" />
      <line x1="5" y1="24" x2="10" y2="24" />
      <line x1="38" y1="24" x2="43" y2="24" />
      <line x1="11.8" y1="11.8" x2="15.3" y2="15.3" />
      <line x1="32.7" y1="32.7" x2="36.2" y2="36.2" />
      <line x1="11.8" y1="36.2" x2="15.3" y2="32.7" />
      <line x1="32.7" y1="15.3" x2="36.2" y2="11.8" />
    </svg>
  );
}

// Bottom hint shared by the waiting screens: max brightness + keep screen on.
function BrightnessHint() {
  return (
    <div style={brightnessHintStyle}>
      <SunIcon size={46} />
      <div style={{ fontSize: '1.15rem', fontWeight: 700, maxWidth: 320, textAlign: 'center', lineHeight: 1.35 }}>
        {t('maxBrightness')}
      </div>
    </div>
  );
}

function IdleScreen({ pipeName }) {
  return (
    <div style={screenStyle}>
      <Logo />
      <div style={{ opacity: 0.6 }}>
        {t('waitingFor')} <strong>{pipeName}</strong> {t('toStart')}
      </div>
      <Spinner />
      <BrightnessHint />
      <div style={{ position: 'absolute', bottom: 12, fontSize: '0.65rem', opacity: 0.25 }}>
        v{APP_VERSION}
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div style={screenStyle}>
      <Logo />
      <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>{t('loadingShow')}</div>
      <Spinner />
    </div>
  );
}

function ReadyScreen() {
  return (
    <div style={screenStyle}>
      <Logo />
      <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', textAlign: 'center', padding: '0 1.5rem' }}>
        {t('holdPhoneUpWaiting')}
      </div>
      <div style={{ opacity: 0.6 }}>{t('startingSoon')}</div>
      <Spinner />
      <BrightnessHint />
    </div>
  );
}

// Client-only READY pulse: once the phone is synced and knows its sector/row,
// it breathes the pipe's brand color while waiting for the operator to start
// the countdown — a "you're in, hold your phone up" signal. The wall never
// renders this (it keeps showing posts until countdown). Color is pipe-
// configurable (lightShowLayer.readyPulseColor), defaulting to pipeup green.
function ReadyPulse({ color }) {
  return (
    <div style={{ ...screenStyle, overflow: 'hidden' }}>
      <div style={{ ...readyPulseLayerStyle, background: color }} />
      <div style={readyPulseContentStyle}>
        <Logo />
        <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem', textAlign: 'center', padding: '0 1.5rem', textShadow: readyPulseTextShadow }}>
          {t('holdPhoneUpWaiting')}
        </div>
        <div style={{ opacity: 0.85, textShadow: readyPulseTextShadow }}>{t('startingSoon')}</div>
      </div>
      <BrightnessHint />
      <style>{'@keyframes pipeupPulse { 0%, 100% { opacity: 0.2; } 50% { opacity: 0.85; } }'}</style>
    </div>
  );
}

function CompletedScreen({ noRedirect, pipeName, endConfig }) {
  // Where this show sends people, if anywhere (MM #178). Both the automatic
  // redirect and the manual button below must agree on it — a button that
  // still pointed at the pipe while the show was configured elsewhere would be
  // the worst of both.
  const target = pipeName
    ? resolveEndRedirect(endConfig, `${PIPE_BASE_URL}/${pipeName}`)
    : null;
  // A show configured to stay put (redirect: 'none') is the audience's last
  // screen — promising a redirect there would be a lie.
  const staying = !willRedirect(endConfig);
  return (
    <div style={screenStyle}>
      <Logo />
      <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem', textAlign: 'center', padding: '0 1.5rem' }}>
        {resolveEndText(endConfig, t('thanks'))}
      </div>
      {/*
        A tappable way back that depends on no timer at all (MM #111). The
        automatic redirect is timer- and visibility-driven and can still be
        missed — a phone that stays locked until long after the show, a browser
        that killed the tab's JS. This is the path that always works, and it is
        the primary call to action rather than a fallback: the whole point of
        the moment is getting people into the app.

        `target` is null when the show stays put (redirect: 'none'), so the
        button disappears on its own there — there is nowhere to send anyone.
      */}
      {!noRedirect && target ? (
        <a
          href={target}
          style={{
            marginTop: '1.5rem',
            padding: '0.85rem 1.6rem',
            borderRadius: '999px',
            background: '#fff',
            color: '#000',
            fontSize: '1rem',
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          {t('backToApp')}
        </a>
      ) : null}
      {/*
        And nothing at all underneath when the show stays put: there is no
        navigation to announce, and the closing text IS the message. The
        `returningToIdle` string is literally '...' in both languages — a
        dev-only placeholder that was never meant to face an audience, and
        did not until `redirect: 'none'` became a real option. MM #178.
      */}
      {staying ? null : (
        <div style={{ opacity: 0.4, marginTop: '1rem', fontSize: '0.9rem' }}>
          {noRedirect ? t('returningToIdle') : t('redirecting')}
        </div>
      )}
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div style={screenStyle}>
      <Logo />
      <div style={{ opacity: 0.6 }}>{message}</div>
    </div>
  );
}

function StaticCountdown() {
  return (
    <div style={screenStyle}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div style={{ fontSize: '4rem', marginBottom: '1rem', lineHeight: 1.2 }}>
          <span style={{ position: 'relative' }}>
            🙋
            <span style={{ position: 'absolute', left: '-0.35em', top: '-0.75em', fontSize: '1em' }}>📱</span>
          </span>
        </div>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>
          {t('holdPhoneUp')}
        </div>
        <div style={{ opacity: 0.7 }}>{t('startingIn')}</div>
      </div>
      <div
        style={{
          fontSize: 'min(40vw, 180px)',
          fontWeight: 700,
          lineHeight: 1,
          color: '#ff653f',
          textShadow: '0 0 40px rgba(255,101,63,0.6)',
        }}
      >
        3
      </div>
      <div style={{ position: 'absolute', bottom: 32, fontSize: '0.875rem', opacity: 0.6 }}>
        {t('keepScreenOn')}
      </div>
      <div style={{ position: 'absolute', top: 16, right: 16, fontSize: '0.75rem', opacity: 0.4 }}>
        DEBUG
      </div>
    </div>
  );
}

function NoPipeScreen() {
  return (
    <div style={screenStyle}>
      <Logo />
      <div style={{ opacity: 0.6 }}>
        {t('navigateTo')} <code>/your-pipe-name</code> {t('toJoinShow')}
      </div>
    </div>
  );
}

// Pipeup "two green corners" loader — the app's rotating clamp animation,
// reproduced self-contained (SVG + CSS, no anime.js).
function Spinner() {
  return (
    <div style={spinnerStyle}>
      <svg viewBox="0 0 200 200" width="72" height="72" style={clampsStyle} aria-hidden="true">
        <g fill="#7ed321" fillRule="evenodd">
          <path d="m0 19.4805195v-19.4805195h19.4805195v6.49350649h-12.98701301v12.98701301z" />
          <path d="m180.519481 200v-19.480519h19.480519v6.493506h-12.987013v12.987013z" transform="matrix(-1 -0 0 -1 380.5194 380.5194)" />
        </g>
      </svg>
      <style>{`@keyframes pipeupClamps { from { transform: rotate(0deg); } to { transform: rotate(-270deg); } }`}</style>
    </div>
  );
}

const logoStyle = {
  width: 160,
  height: 'auto',
  marginBottom: '2rem',
};

const screenStyle = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#000',
  color: '#fff',
};

const spinnerStyle = {
  marginTop: '2rem',
  width: 72,
  height: 72,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const clampsStyle = {
  animation: 'pipeupClamps 1s cubic-bezier(0.86, 0, 0.07, 1) infinite alternate',
  transformOrigin: 'center',
  display: 'block',
};

const brightnessHintStyle = {
  position: 'absolute',
  bottom: 44,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '0.55rem',
  // Raised from 0.55: this is an instruction people must act on, and at 0.55
  // the bolder, larger text still read as disabled.
  color: 'rgba(255, 255, 255, 0.75)',
};

// Full-screen breathing color layer behind the READY content.
const readyPulseLayerStyle = {
  position: 'absolute',
  inset: 0,
  animation: 'pipeupPulse 1.8s ease-in-out infinite',
  pointerEvents: 'none',
};

const readyPulseContentStyle = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
};

const readyPulseTextShadow = '0 1px 6px rgba(0, 0, 0, 0.55)';


export default function App() {
  const pipeName = getPipeName();
  const urlAssignment = useRef(getUrlAssignment()).current;
  const saved = useRef(pipeName ? getSavedAssignment(pipeName) : { zone: null, row: null }).current;
  // URL params take priority over localStorage
  const initialAssignment = useRef({
    zone: urlAssignment.zone ?? saved.zone,
    row: urlAssignment.row ?? saved.row,
  }).current;
  const [showState, setShowState] = useState(SHOW_STATES.IDLE);
  const [schedule, setSchedule] = useState(null);
  const [script, setScript] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [zoneIndex, setZoneIndex] = useState(initialAssignment.zone);
  const [rowIndex, setRowIndex] = useState(initialAssignment.row);
  // Color pulsed on the READY waiting screen — pipe-configurable, defaults to
  // pipeup green. Set from the schedule payload at the start of prepareAndPlay.
  const [pulseColor, setPulseColor] = useState('#7ED321');

  // One CueEngine for the whole session. Lazily constructed so a re-render
  // never throws away decoded audio (MM #116).
  const cueEngineRef = useRef(null);
  if (!cueEngineRef.current) cueEngineRef.current = new CueEngine();
  const cueEngine = cueEngineRef.current;
  const [cuesMuted, setCuesMuted] = useState(false);
  const [hasCues, setHasCues] = useState(false);
  const showInstanceRef = useRef(null);
  const completedRef = useRef(false);
  const zoneResolveRef = useRef(null);
  // Per-attempt token. The supervisor effect bumps this to invalidate an
  // in-flight prepareAndPlay when the current show is cancelled or replaced.
  const attemptRef = useRef(0);
  // Screen-awake telemetry: how often / how long the page was hidden (proxy for
  // a phone that auto-locked or got backgrounded during the show).
  const hiddenCountRef = useRef(0);
  const hiddenMsRef = useRef(0);
  const hiddenSinceRef = useRef(0);

  const participant = useParticipant();

  // Keep the screen awake for the whole time the viewer is on the lightshow page
  // — phone auto-lock/dim would blank the show. Best-effort: re-acquire on any
  // tap (some browsers require a gesture) and on tab re-show (handled in wakeLock).
  useEffect(() => {
    wakeLock.request();
    const reacquire = () => wakeLock.request();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        hiddenCountRef.current += 1;
        hiddenSinceRef.current = Date.now();
      } else if (hiddenSinceRef.current) {
        hiddenMsRef.current += Date.now() - hiddenSinceRef.current;
        hiddenSinceRef.current = 0;
      }
    };
    window.addEventListener('pointerdown', reacquire);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pointerdown', reacquire);
      document.removeEventListener('visibilitychange', onVisibility);
      wakeLock.release();
    };
  }, []);

  // --- Poll schedule only while idle ---
  const { schedule: polledSchedule, error: pollError } = useSchedulePoller(
    pipeName,
    showState !== SHOW_STATES.IDLE // pause when not idle
  );

  // When poller detects an active show, kick off the preparation flow
  useEffect(() => {
    if (!polledSchedule) return;
    if (showState !== SHOW_STATES.IDLE) return;

    const active = ['distributing', 'countdown', 'paused', 'playing'].includes(
      polledSchedule.status
    );
    if (!active) return;

    // New show detected — start the preparation sequence
    showInstanceRef.current = polledSchedule.showInstanceId;
    setSchedule(polledSchedule);
    prepareAndPlay(polledSchedule);
  }, [polledSchedule]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Sequential preparation: fetch script → sync time → wait for countdown → play.
   * All in one async flow — no complex state machine interplay.
   */
  const prepareAndPlay = useCallback(
    async (initialSchedule) => {
      const { pipeId, showInstanceId } = initialSchedule;
      // Claim this attempt. If the supervisor later detects the show was
      // cancelled or replaced, it bumps attemptRef and aborted() becomes true,
      // so every step below bails out instead of driving a dead show forward.
      const myAttempt = ++attemptRef.current;
      const aborted = () => attemptRef.current !== myAttempt;
      showInstanceRef.current = showInstanceId;
      completedRef.current = false;
      if (initialSchedule.readyPulseColor) setPulseColor(initialSchedule.readyPulseColor);
      console.log('[Lightshow] prepareAndPlay', { pipeId, showInstanceId, status: initialSchedule.status });

      try {
        // 0. Register participant
        try {
          console.log('[Lightshow] Registering participant...');
          await participant.register(pipeId, showInstanceId);
          console.log('[Lightshow] Registered OK');
        } catch (err) {
          console.warn('[Lightshow] Registration failed:', err);
        }

        // 1. Load script
        setShowState(SHOW_STATES.LOADING);

        const scriptRes = await apiFetch(API.script(initialSchedule.scriptId, initialSchedule.scriptVersion));
        if (!scriptRes.ok) throw new Error(`Script fetch failed: ${scriptRes.status}`);
        const scriptData = await scriptRes.json();
        setScript(scriptData);

        // Cues: build the list and pull the BYTES now. This is typically well
        // before the countdown, and each phone loads at its own moment, so the
        // requests spread out instead of bunching at showtime. Deliberately not
        // awaited — a slow clip must delay sound, never the lightshow. Decoding
        // happens on the first user gesture (see unlockCues).
        const cueCount = cueEngine.build(scriptData.cueCode, {
          params: scriptData.paramValues || {},
          duration: (scriptData.duration || 0) / 1000,
        });
        setHasCues(cueCount > 0);
        if (cueCount > 0 && scriptData.audioClips) {
          cueEngine.prefetch(scriptData.audioClips).catch(() => {});
        }

        // Report loaded status
        try {
          await participant.updateStatus(pipeId, showInstanceId, 'loaded');
        } catch (_) {}

        if (aborted()) return;

        // 2. Sync time
        setShowState(SHOW_STATES.SYNCING);
        await timeSync.sync();
        timeSync.startPeriodicSync();

        if (aborted()) return;

        // 2b. Zone/row selection (if any selection enabled). A plain option
        // list (inputType 'select', sectorEnabled false) still requires a pick —
        // see zoneSelectionNeeded (the "Flaggen A und B" skip-the-picker bug).
        const zc = scriptData.zoneConfig;
        const { needsSelection, hasSector, hasRow } = zoneSelectionNeeded(zc);
        if (needsSelection) {
          // Only URL params auto-skip the selector; saved values just pre-fill it
          const sectorDone = !hasSector || urlAssignment.zone !== null;
          const rowDone = !hasRow || urlAssignment.row !== null;
          const preAssigned = sectorDone && rowDone;

          if (preAssigned) {
            // URL params cover all selections — report to server, skip selector
            console.log('[Lightshow] Zone pre-assigned via URL', urlAssignment);
            try {
              await participant.selectZone(
                pipeId, showInstanceId,
                urlAssignment.zone ?? 0, urlAssignment.row ?? null,
                timeSync.offset
              );
            } catch (_) {}
          } else {
            setShowState(SHOW_STATES.ZONE_SELECT);

            // If saved selection covers all requirements, continue flow immediately
            // (selector stays visible so user can change during countdown)
            const savedSectorDone = !hasSector || initialAssignment.zone !== null;
            const savedRowDone = !hasRow || initialAssignment.row !== null;
            if (savedSectorDone && savedRowDone) {
              console.log('[Lightshow] Zone pre-filled from saved selection', initialAssignment);
              try {
                await participant.selectZone(
                  pipeId, showInstanceId,
                  initialAssignment.zone ?? 0, initialAssignment.row ?? null,
                  timeSync.offset
                );
              } catch (_) {}
            } else {
              // Wait until all required selections are made. The supervisor can
              // also resolve this ref to unblock a parked client when the show
              // is cancelled/replaced (aborted() then catches it below).
              await new Promise((resolve) => {
                zoneResolveRef.current = resolve;
              });
              zoneResolveRef.current = null;
              if (aborted()) return;
              // selectZone with latest values happens in handleZoneSelect
            }
          }
        } else {
          // No zones — report ready immediately
          try {
            await participant.updateStatus(pipeId, showInstanceId, 'ready', {
              syncOffset: timeSync.offset,
            });
          } catch (_) {}
        }

        if (aborted()) return;

        // Synced + sector/row known + 'ready' reported → pulse the brand color
        // while waiting for the operator to start the countdown. The wall never
        // reaches this phase (it stays on posts until countdown).
        setShowState(SHOW_STATES.READY);

        // 3. Poll for latest schedule to get accurate timing
        const freshSchedule = await fetchScheduleOnce(pipeName);
        if (aborted()) return;
        if (
          !freshSchedule ||
          freshSchedule.status === 'idle' ||
          freshSchedule.showInstanceId !== showInstanceId
        ) {
          // Show was cancelled or replaced during preparation
          setShowState(SHOW_STATES.IDLE);
          return;
        }
        setSchedule(freshSchedule);

        // 4. Wait for countdown / playing ('paused' = a held countdown, MM #190)
        if (
          freshSchedule.status === 'distributing' ||
          freshSchedule.status === 'countdown' ||
          freshSchedule.status === 'paused'
        ) {
          // If already in (or holding) countdown, show it immediately
          if (
            (freshSchedule.status === 'countdown' && freshSchedule.startTime) ||
            freshSchedule.status === 'paused'
          ) {
            setShowState(SHOW_STATES.COUNTDOWN);
            setSchedule(freshSchedule);
          }

          // Poll until status becomes "playing" (or "idle" = cancelled).
          // Updates the countdown display as soon as it starts, and holds it
          // frozen while the operator has paused.
          const playSchedule = await waitForPlaying(
            pipeName,
            showInstanceId,
            aborted,
            (s) => {
              if ((s.status === 'countdown' && s.startTime) || s.status === 'paused') {
                setShowState(SHOW_STATES.COUNTDOWN);
                setSchedule(s);
              }
            },
            // Start exactly at showStartTime (when the countdown hits 0), not
            // when the polled 'playing' flip arrives 0-2s later.
            { getServerTime: () => timeSync.getServerTime() }
          );
          if (aborted()) return;
          if (!playSchedule) {
            // Show was cancelled or replaced
            setShowState(SHOW_STATES.IDLE);
            return;
          }
          setSchedule(playSchedule);
        }

        if (aborted()) return;

        // 5. Play
        setShowState(SHOW_STATES.PLAYING);

        // Report playing status. Enqueued immediately — the gateway batcher
        // buffers it and flushes on a desynchronised rolling timer, so the
        // synchronised "playing" instant no longer bursts the write-path.
        participant.updateStatus(pipeId, showInstanceId, 'playing').catch(() => {});
      } catch (err) {
        setErrorMsg(err.message || t('failedToPrepare'));
        setShowState(SHOW_STATES.ERROR);
        timeSync.stopPeriodicSync();

        // Report failure
        try {
          await participant.updateStatus(pipeId, showInstanceId, 'failed', {
            errorMessage: err.message,
          });
        } catch (_) {}
      }
    },
    [pipeName, participant]
  );

  const handleComplete = useCallback((engineTelemetry) => {
    completedRef.current = true;
    setShowState(SHOW_STATES.COMPLETED);
    timeSync.stopPeriodicSync();

    // Report completed status. Enqueued immediately — the gateway batcher
    // desynchronises the flush, so this synchronised end-of-show transition
    // no longer bursts the write-path. The post-show redirect below is delayed
    // past the flush window so the batch is sent before navigation.
    if (schedule?.pipeId && schedule?.showInstanceId) {
      participant.updateStatus(schedule.pipeId, schedule.showInstanceId, 'completed').catch(() => {});
    }

    // Screen-awake telemetry — sampled (~20%) to keep the payload small.
    // Captures Wake Lock support + involuntary releases + how often/long the
    // page was hidden, so we can measure real auto-lock/dim drop-off.
    if (schedule?.pipeId && schedule?.showInstanceId && Math.random() < 0.2) {
      participant.sendTelemetry(schedule.pipeId, schedule.showInstanceId, {
        wakeLockSupported: wakeLock.isSupported() ? 1 : 0,
        wakeLockReleases: wakeLock.releaseCount,
        hiddenCount: hiddenCountRef.current,
        hiddenMs: hiddenMsRef.current,
        // Quality metrics — populate the aggregate fields that were always null.
        ...(timeSync.getQuality() !== null
          ? { syncQuality: timeSync.getQuality() }
          : {}),
        ...(engineTelemetry?.playbackAccuracy != null
          ? { playbackAccuracy: engineTelemetry.playbackAccuracy }
          : {}),
      }).catch(() => {});
    }

    // Reset to idle after 3s when in dev mode or ?noRedirect is set.
    // In prod: redirect to the pipe after the completed-ping window, plus extra
    // spread, so the ping always fires before navigation AND the redirect burst
    // is itself spread out.
    if (import.meta.env.DEV || urlAssignment.noRedirect) {
      setTimeout(() => {
        setShowState(SHOW_STATES.IDLE);
        setSchedule(null);
        setScript(null);
        setZoneIndex(initialAssignment.zone);
        setRowIndex(initialAssignment.row);
      }, 3000);
      return;
    }

    // Where to go is the show's decision (MM #178); null = stay on the closing
    // screen, so nothing is scheduled at all and the phone simply remains here.
    const target = resolveEndRedirect(schedule?.endConfig, `${PIPE_BASE_URL}/${pipeName}`);
    if (!target) return;

    // MM #111. Measured after LKF Day2: 334 devices reported `completed`, and
    // ZERO posts arrived in the seven minutes that followed. The page was
    // alive at the end of the show — it was the redirect that never ran.
    //
    // It used to fire 12-20s after the end, purely so the rolling 10s batch
    // flush had sent the completed ping first. But that is exactly when
    // people pocket or lock the phone, and iOS suspends timers in a hidden
    // tab, so for a pocketed phone the redirect simply never happened.
    //
    // flushBeacon removes the reason to wait: sendBeacon is delivered by the
    // browser independently of the page, so the ping survives navigation.
    participant.flushBeacon();

    const go = () => {
      window.location.href = target;
    };

    // Still jittered, just an order of magnitude sooner: 676 clients landing
    // on the pipe at once is worth spreading, and the burst protection this
    // replaces was aimed at the write path, not a CDN-served page view.
    const delay = 800 + Math.random() * 1700;
    const timer = setTimeout(() => {
      // A hidden tab cannot navigate meaningfully and the timer may not even
      // have run on time. Defer to the moment the user looks at the phone
      // again — that is the whole failure this fixes.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      go();
    }, delay);

    // The phone was already away when the show ended: go as soon as it comes
    // back. Without this, the entire pocketed cohort is lost.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVisible);
        clearTimeout(timer);
        go();
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisible);
    }
  }, [schedule, participant, pipeName]);

  // --- Playing heartbeat: while the choreo runs, ping periodically so the
  // server can sample PEAK CONCURRENT playing (the trustworthy headline metric,
  // measured in steady state — vs the lossy end-of-show completion snapshot).
  // Jittered per client so the pings stay desynchronised; rides the batch
  // buffer so it's cheap at scale. ---
  useEffect(() => {
    if (showState !== SHOW_STATES.PLAYING) return undefined;
    if (!schedule?.pipeId || !schedule?.showInstanceId) return undefined;

    let timer = null;
    const beat = () => {
      participant.heartbeat(schedule.pipeId, schedule.showInstanceId);
      // 0.5×–1.5× of the interval, re-rolled each tick.
      timer = setTimeout(beat, PLAYING_HEARTBEAT_MS * (0.5 + Math.random()));
    };
    // First beat at a random phase so clients that started together desync.
    timer = setTimeout(beat, Math.random() * PLAYING_HEARTBEAT_MS);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showState, schedule, participant]);

  // --- Supervisor: while parked in ANY active (non-idle) state, watch the
  // schedule for the current show being cancelled (status → idle) OR replaced
  // by a different show (new showInstanceId). Either way, abort the in-flight
  // attempt and return to IDLE, which re-arms the poller so the new show is
  // picked up cleanly. Fixes clients getting stuck when an operator cancels a
  // show (e.g. during zone selection) and immediately schedules a new one. ---
  useEffect(() => {
    const ACTIVE = [
      SHOW_STATES.LOADING,
      SHOW_STATES.SYNCING,
      SHOW_STATES.ZONE_SELECT,
      SHOW_STATES.READY,
      SHOW_STATES.COUNTDOWN,
      SHOW_STATES.PLAYING,
    ];
    if (!ACTIVE.includes(showState) || !pipeName) return;

    const supervise = async () => {
      let data;
      try {
        data = await fetchScheduleOnce(pipeName);
      } catch (_) {
        return; // transient network error — keep waiting
      }

      const gone = !data || data.status === 'idle';
      const replaced =
        data &&
        data.showInstanceId &&
        showInstanceRef.current &&
        data.showInstanceId !== showInstanceRef.current;

      if (!gone && !replaced) return;

      // During PLAYING, an idle status is the NORMAL end of a show once the
      // engine has finished (completedRef set first). Only a still-running
      // engine seeing idle is an emergency stop. Don't reset on normal finish.
      if (showState === SHOW_STATES.PLAYING && gone && completedRef.current) {
        return;
      }

      // Abort the current prepare attempt and re-arm for the (possibly new) show.
      attemptRef.current += 1;
      if (zoneResolveRef.current) {
        zoneResolveRef.current(); // unblock a client parked on zone selection
        zoneResolveRef.current = null;
      }
      showInstanceRef.current = null;
      completedRef.current = false;
      setScript(null);
      setSchedule(null);
      setShowState(SHOW_STATES.IDLE);
    };

    // Jittered — see MM #73. This is the second synchronised 2s poller.
    const cancel = startJitteredPoll(supervise, SCHEDULE_POLL_INTERVAL);
    return cancel;
  }, [showState, pipeName]);

  const handleError = useCallback((err) => {
    setErrorMsg(err?.message || t('playbackError'));
    setShowState(SHOW_STATES.ERROR);
    timeSync.stopPeriodicSync();

    // Report failure
    if (schedule?.pipeId && schedule?.showInstanceId) {
      participant.updateStatus(schedule.pipeId, schedule.showInstanceId, 'failed', {
        errorMessage: err?.message,
      }).catch(() => {});
    }
  }, [schedule, participant]);

  /**
   * Create/resume the AudioContext. MUST run inside a real user gesture —
   * mobile browsers start it suspended and silently ignore playback otherwise.
   * Anything already prefetched is decoded here (MM #116).
   */
  const unlockCues = useCallback(() => {
    cueEngine.unlock();
  }, [cueEngine]);

  // Not every show has a zone selector, so the selector tap cannot be the only
  // unlock point — otherwise those shows would be silent for everyone. Any tap
  // anywhere counts. Passive + once: this must never cost a frame, and after
  // the first gesture the context stays.
  useEffect(() => {
    const unlock = () => cueEngine.unlock();
    window.addEventListener('pointerdown', unlock, { once: true, passive: true });
    window.addEventListener('touchstart', unlock, { once: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('touchstart', unlock);
    };
  }, [cueEngine]);

  const handleZoneSelect = useCallback(({ zoneIndex: zi, rowIndex: ri }) => {
    unlockCues();
    setZoneIndex(zi);
    setRowIndex(ri ?? null);

    // Persist selection for next visit
    if (pipeName) saveAssignment(pipeName, zi, ri ?? null);

    // Check if all required selections are made to resolve the preparation promise
    if (zoneResolveRef.current) {
      const zoneConfig = script?.zoneConfig || {};
      const { hasSector, hasRow } = zoneSelectionNeeded(zoneConfig);
      const sectorDone = !hasSector || zi !== null;
      const rowDone = !hasRow || ri !== null;
      if (sectorDone && rowDone) {
        zoneResolveRef.current({ zoneIndex: zi, rowIndex: ri });
        zoneResolveRef.current = null;
      }
    }

    // Report selection to server
    if (schedule?.pipeId && schedule?.showInstanceId && (zi !== null || ri !== null)) {
      participant.selectZone(schedule.pipeId, schedule.showInstanceId, zi ?? 0, ri ?? null, timeSync.offset).catch(() => {});
    }
  }, [schedule, participant, script]);

  if (!pipeName) return <NoPipeScreen />;

  // Debug modes — bypass normal flow
  if (urlAssignment.debug1) {
    const debugZoneConfig = script?.zoneConfig || {
      enabled: true,
      sectorEnabled: true,
      label: t('selectSector'),
      inputType: 'select',
      options: ['Sector A', 'Sector B', 'Sector C'],
      rowEnabled: true,
      rowLabel: t('enterRow'),
      rowMin: 1,
      rowMax: 30,
    };
    return (
      <ZoneSelector
        zoneConfig={debugZoneConfig}
        onSelect={({ zoneIndex: zi, rowIndex: ri }) => {
          unlockCues();
          setZoneIndex(zi);
          setRowIndex(ri ?? null);
        }}
        selectedIndex={zoneIndex}
        selectedRow={rowIndex}
      />
    );
  }

  if (urlAssignment.debug2) {
    return <StaticCountdown />;
  }

  if (urlAssignment.debug3) {
    return <CompletedScreen noRedirect />;
  }

  if (pollError && showState === SHOW_STATES.IDLE) {
    return <ErrorScreen message={pollError} />;
  }

  switch (showState) {
    case SHOW_STATES.IDLE:
      return <IdleScreen pipeName={pipeName} />;

    case SHOW_STATES.LOADING:
      return <LoadingScreen />;

    case SHOW_STATES.SYNCING:
      return <ReadyScreen />;

    case SHOW_STATES.ZONE_SELECT:
      return (
        <ZoneSelector
          zoneConfig={script?.zoneConfig || {}}
          onSelect={handleZoneSelect}
          selectedIndex={zoneIndex}
          selectedRow={rowIndex}
        />
      );

    case SHOW_STATES.READY:
      return <ReadyPulse color={pulseColor} />;

    case SHOW_STATES.COUNTDOWN:
      return (
        <Countdown
          targetTime={schedule?.startTime || schedule?.countdownStartTime}
          getServerTime={() => timeSync.getServerTime()}
          paused={schedule?.status === 'paused'}
          remainingMs={schedule?.remainingMs}
        />
      );

    case SHOW_STATES.PLAYING:
      return (
        <Canvas
          script={script}
          startTime={schedule?.startTime}
          syncOffset={timeSync.offset}
          getSyncOffset={() => timeSync.offset}
          zoneIndex={zoneIndex}
          zoneCount={script?.zoneConfig?.options?.length || (script?.zoneConfig?.max != null ? script.zoneConfig.max - (script.zoneConfig.min ?? 0) + 1 : 0)}
          zoneOptions={script?.zoneConfig?.options || []}
          rowIndex={rowIndex}
          cueEngine={hasCues ? cueEngine : null}
          onComplete={handleComplete}
          onError={handleError}
        />
      );

    case SHOW_STATES.COMPLETED:
      return (
        <CompletedScreen
          noRedirect={import.meta.env.DEV || urlAssignment.noRedirect}
          pipeName={pipeName}
          endConfig={schedule?.endConfig}
        />
      );

    case SHOW_STATES.ERROR:
      return <ErrorScreen message={errorMsg || t('somethingWrong')} />;

    default:
      return <IdleScreen pipeName={pipeName} />;
  }
}

// fetchScheduleOnce + waitForPlaying live in ./lib/schedulePolling (imported
// above) so the re-arm exit conditions can be unit-tested in isolation.
